import {
  doc,
  getDoc,
  setDoc,
  updateDoc,
  collection,
  query,
  where,
  getDocs,
  orderBy,
  Timestamp,
  serverTimestamp,
  increment,
  runTransaction,
} from 'firebase/firestore';
import { db } from '../config/firebase';

// Types
export type UserRole = 'platform_admin' | 'cra_admin' | 'cra_analyst' | 'rating_committee' | 'bank_admin' | 'bank_user';
export type UserStatus = 'pending' | 'active' | 'suspended';
export type OrgType = 'platform' | 'cra' | 'bank';
export type OrgStatus = 'active' | 'suspended';
export type AuthProvider = 'google' | 'password';

export interface UserProfile {
  uid: string;
  email: string;
  display_name: string;
  org_id: string | null;
  role: UserRole | null;
  status: UserStatus;
  auth_provider: AuthProvider;
  invited_by: string | null;
  created_at: Timestamp;
  approved_at: Timestamp | null;
  approved_by: string | null;
  last_login: Timestamp;
  photo_url?: string;
}

export interface Organization {
  id: string;
  name: string;
  type: OrgType;
  parent_org_id: string | null;
  status: 'active' | 'suspended';
  licenses: {
    analyst_seats: number;
    bank_seats: number;
    analyst_seats_used: number;
    bank_seats_used: number;
  };
  institutions_access: string[];
  created_at: Timestamp;
  updated_at: Timestamp;
}

export interface AuditLog {
  id: string;
  action: string;
  actor_id: string;
  target_type: 'user' | 'org';
  target_id: string;
  details: Record<string, unknown>;
  timestamp: Timestamp;
}

export interface AnalystAssignment {
  id: string;
  analyst_id: string;
  institution_id: string;
  org_id: string;
  assigned_by: string;
  assigned_at: Timestamp;
  status: 'active' | 'inactive';
  shared_with: string[];
}

// Report Workflow Types
export type ReportWorkflowStatus =
  | 'draft'              // Analyst working on it
  | 'pending_admin'      // Submitted for CRA Admin review
  | 'client_review'      // Admin approved, Bank Admin can comment
  | 'pending_committee'  // Client review complete, awaiting committee
  | 'approved'           // Committee approved (final)
  | 'rejected';          // Rejected at any stage

export interface ReportDeadline {
  final_due_date: Timestamp;
  draft_due: Timestamp | null;
  admin_review_due: Timestamp | null;
  client_review_due: Timestamp | null;
  committee_review_due: Timestamp | null;
}

export interface ClientComment {
  id: string;
  author_id: string;
  author_name: string;
  content: string;
  section_id: string | null;  // Can be linked to a specific section
  created_at: Timestamp;
  resolved: boolean;
  resolved_by: string | null;
  resolved_at: Timestamp | null;
}

export interface SectionDraft {
  id: string;
  title: string;
  content: string;
  ai_generated: boolean;
  edited: boolean;
  last_edited_by: string;
  last_edited_at: Timestamp;
}

export interface ReportDraftContent {
  sections: Record<string, SectionDraft>;
  notes: string;
  attachments: string[];
}

export interface ReportWorkflow {
  id: string;
  institution_id: string;
  period: string;                    // "2024-03"
  analyst_id: string;
  org_id: string;

  status: ReportWorkflowStatus;
  version: number;

  deadline: ReportDeadline;
  content: ReportDraftContent;

  // Timestamps
  created_at: Timestamp;
  created_by: string;
  updated_at: Timestamp;
  updated_by: string;

  // AI Generation tracking
  ai_generated_at: Timestamp | null;
  ai_generation_model: string | null;

  // First edit tracking (set on first save after AI generation)
  first_edited_at: Timestamp | null;
  first_edited_by: string | null;

  // Submission
  submitted_at: Timestamp | null;

  // Admin review
  admin_reviewed_at: Timestamp | null;
  admin_reviewed_by: string | null;
  admin_comments: string | null;

  // Client review
  client_review_started_at: Timestamp | null;
  client_reviewed_at: Timestamp | null;
  client_reviewed_by: string | null;
  client_comments: ClientComment[];
  client_additional_info: string | null;

  // Committee review
  committee_reviewed_at: Timestamp | null;
  committee_reviewed_by: string | null;
  committee_comments: string | null;

  // Final
  published_at: Timestamp | null;

  // Rejection tracking
  rejected_at: Timestamp | null;
  rejected_by: string | null;
  rejection_reason: string | null;
  rejection_stage: 'admin' | 'committee' | null;
}

// User Functions

/**
 * Get user profile by UID
 */
export const getUserProfile = async (uid: string): Promise<UserProfile | null> => {
  const docRef = doc(db, 'users', uid);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return { uid, ...docSnap.data() } as UserProfile;
  }
  return null;
};

/**
 * Create a new user profile (for new sign-ups)
 */
export const createUserProfile = async (
  uid: string,
  email: string,
  displayName: string,
  authProvider: AuthProvider,
  photoUrl?: string
): Promise<UserProfile> => {
  const userProfile: Omit<UserProfile, 'uid'> = {
    email,
    display_name: displayName,
    org_id: null,
    role: null,
    status: 'pending',
    auth_provider: authProvider,
    invited_by: null,
    created_at: Timestamp.now(),
    approved_at: null,
    approved_by: null,
    last_login: Timestamp.now(),
    photo_url: photoUrl,
  };

  const docRef = doc(db, 'users', uid);
  await setDoc(docRef, userProfile);

  return { uid, ...userProfile };
};

/**
 * Update user's last login timestamp
 */
export const updateLastLogin = async (uid: string): Promise<void> => {
  const docRef = doc(db, 'users', uid);
  await updateDoc(docRef, {
    last_login: serverTimestamp(),
  });
};

/**
 * Approve a pending user
 */
export const approveUser = async (
  uid: string,
  orgId: string,
  role: UserRole,
  approvedBy: string
): Promise<void> => {
  const docRef = doc(db, 'users', uid);
  await updateDoc(docRef, {
    org_id: orgId,
    role,
    status: 'active',
    approved_at: serverTimestamp(),
    approved_by: approvedBy,
  });
};

/**
 * Reject/delete a pending user
 */
export const rejectUser = async (uid: string, _rejectedBy?: string): Promise<void> => {
  const docRef = doc(db, 'users', uid);
  await updateDoc(docRef, {
    status: 'suspended',
  });
};

/**
 * Update a user profile
 */
export const updateUserProfile = async (
  uid: string,
  updates: Partial<Pick<UserProfile, 'role' | 'status' | 'org_id' | 'display_name'>>
): Promise<void> => {
  const docRef = doc(db, 'users', uid);
  await updateDoc(docRef, updates);
};

/**
 * Get all pending users
 */
export const getPendingUsers = async (): Promise<UserProfile[]> => {
  const usersRef = collection(db, 'users');
  const q = query(
    usersRef,
    where('status', '==', 'pending'),
    orderBy('created_at', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    uid: doc.id,
    ...doc.data(),
  } as UserProfile));
};

/**
 * Get all users for an organization
 */
export const getUsersByOrg = async (orgId: string): Promise<UserProfile[]> => {
  const usersRef = collection(db, 'users');
  const q = query(
    usersRef,
    where('org_id', '==', orgId),
    orderBy('created_at', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    uid: doc.id,
    ...doc.data(),
  } as UserProfile));
};

/**
 * Get pending users for a specific organization
 * (Users with status=pending and org_id matching)
 */
export const getPendingUsersForOrg = async (orgId: string): Promise<UserProfile[]> => {
  const usersRef = collection(db, 'users');
  const q = query(
    usersRef,
    where('org_id', '==', orgId),
    where('status', '==', 'pending'),
    orderBy('created_at', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    uid: doc.id,
    ...doc.data(),
  } as UserProfile));
};

/**
 * Get all users (for platform admin)
 */
export const getAllUsers = async (): Promise<UserProfile[]> => {
  const usersRef = collection(db, 'users');
  const q = query(usersRef, orderBy('created_at', 'desc'));

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    uid: doc.id,
    ...doc.data(),
  } as UserProfile));
};

// Organization Functions

/**
 * Get organization by ID
 */
export const getOrganization = async (orgId: string): Promise<Organization | null> => {
  const docRef = doc(db, 'organizations', orgId);
  const docSnap = await getDoc(docRef);

  if (docSnap.exists()) {
    return { id: orgId, ...docSnap.data() } as Organization;
  }
  return null;
};

/**
 * Create a new organization
 */
export const createOrganization = async (
  org: Omit<Organization, 'id' | 'created_at' | 'updated_at'>
): Promise<Organization> => {
  const orgsRef = collection(db, 'organizations');
  const orgId = `org_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const orgData = {
    ...org,
    created_at: serverTimestamp(),
    updated_at: serverTimestamp(),
  };

  const docRef = doc(orgsRef, orgId);
  await setDoc(docRef, orgData);

  return {
    id: orgId,
    ...org,
    created_at: Timestamp.now(),
    updated_at: Timestamp.now(),
  };
};

/**
 * Update an organization
 */
export const updateOrganization = async (
  orgId: string,
  updates: Partial<Organization>
): Promise<void> => {
  const docRef = doc(db, 'organizations', orgId);
  await updateDoc(docRef, {
    ...updates,
    updated_at: serverTimestamp(),
  });
};

/**
 * Get all organizations
 */
export const getAllOrganizations = async (): Promise<Organization[]> => {
  const orgsRef = collection(db, 'organizations');
  const q = query(orgsRef, orderBy('created_at', 'desc'));

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Organization));
};

/**
 * Get organizations by type
 */
export const getOrganizationsByType = async (type: OrgType): Promise<Organization[]> => {
  const orgsRef = collection(db, 'organizations');
  const q = query(
    orgsRef,
    where('type', '==', type),
    orderBy('created_at', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Organization));
};

/**
 * Get child organizations (banks under a CRA)
 */
export const getChildOrganizations = async (parentOrgId: string): Promise<Organization[]> => {
  const orgsRef = collection(db, 'organizations');
  const q = query(
    orgsRef,
    where('parent_org_id', '==', parentOrgId),
    orderBy('created_at', 'desc')
  );

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Organization));
};

// Audit Log Functions

/**
 * Create an audit log entry
 */
export const createAuditLog = async (
  action: string,
  actorId: string,
  targetType: 'user' | 'org',
  targetId: string,
  details: Record<string, unknown> = {}
): Promise<void> => {
  const logsRef = collection(db, 'audit_logs');
  const logId = `log_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;

  const logData = {
    action,
    actor_id: actorId,
    target_type: targetType,
    target_id: targetId,
    details,
    timestamp: serverTimestamp(),
  };

  const docRef = doc(logsRef, logId);
  await setDoc(docRef, logData);
};

/**
 * Get recent audit logs
 */
export const getRecentAuditLogs = async (limit = 50): Promise<AuditLog[]> => {
  const logsRef = collection(db, 'audit_logs');
  const q = query(logsRef, orderBy('timestamp', 'desc'));

  const querySnapshot = await getDocs(q);
  return querySnapshot.docs.slice(0, limit).map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as AuditLog));
};

// Seat Management Functions

/**
 * Determine which seat type a role uses
 * - CRA roles (cra_admin, cra_analyst, rating_committee) consume analyst seats
 * - Bank roles (bank_admin, bank_user) consume bank seats
 * - Platform admin doesn't consume any seats
 */
export const getSeatTypeForRole = (role: UserRole): 'analyst' | 'bank' | null => {
  // CRA roles consume analyst seats (includes rating_committee)
  if (role === 'cra_admin' || role === 'cra_analyst' || role === 'rating_committee') {
    return 'analyst';
  }
  // Bank roles consume bank seats
  if (role === 'bank_admin' || role === 'bank_user') {
    return 'bank';
  }
  // Platform admin doesn't consume seats
  return null;
};

/**
 * Check if a seat is available for a given role in an organization
 */
export const checkSeatAvailability = async (
  orgId: string,
  role: UserRole
): Promise<{ available: boolean; used: number; total: number; seatType: 'analyst' | 'bank' | null }> => {
  const seatType = getSeatTypeForRole(role);

  // Admin roles don't consume seats
  if (!seatType) {
    return { available: true, used: 0, total: 0, seatType: null };
  }

  const org = await getOrganization(orgId);
  if (!org) {
    throw new Error(`Organization ${orgId} not found`);
  }

  const used = seatType === 'analyst'
    ? org.licenses.analyst_seats_used
    : org.licenses.bank_seats_used;
  const total = seatType === 'analyst'
    ? org.licenses.analyst_seats
    : org.licenses.bank_seats;

  return {
    available: used < total,
    used,
    total,
    seatType,
  };
};

/**
 * Increment seat usage when approving a user (atomic operation)
 */
export const incrementSeatUsage = async (
  orgId: string,
  seatType: 'analyst' | 'bank'
): Promise<void> => {
  const docRef = doc(db, 'organizations', orgId);
  const field = seatType === 'analyst' ? 'licenses.analyst_seats_used' : 'licenses.bank_seats_used';

  await updateDoc(docRef, {
    [field]: increment(1),
    updated_at: serverTimestamp(),
  });
};

/**
 * Decrement seat usage when suspending or removing a user (atomic operation)
 */
export const decrementSeatUsage = async (
  orgId: string,
  seatType: 'analyst' | 'bank'
): Promise<void> => {
  const docRef = doc(db, 'organizations', orgId);
  const field = seatType === 'analyst' ? 'licenses.analyst_seats_used' : 'licenses.bank_seats_used';

  await updateDoc(docRef, {
    [field]: increment(-1),
    updated_at: serverTimestamp(),
  });
};

/**
 * Approve a user with seat validation (transactional)
 * This is a safer alternative to the basic approveUser that validates seats first
 */
export const approveUserWithSeatValidation = async (
  uid: string,
  orgId: string,
  role: UserRole,
  approvedBy: string
): Promise<{ success: boolean; error?: string }> => {
  const seatType = getSeatTypeForRole(role);

  // If role doesn't consume seats, just approve directly
  if (!seatType) {
    await approveUser(uid, orgId, role, approvedBy);
    return { success: true };
  }

  // Use transaction to check and update atomically
  try {
    await runTransaction(db, async (transaction) => {
      const orgRef = doc(db, 'organizations', orgId);
      const orgDoc = await transaction.get(orgRef);

      if (!orgDoc.exists()) {
        throw new Error(`Organization ${orgId} not found`);
      }

      const orgData = orgDoc.data() as Omit<Organization, 'id'>;
      const used = seatType === 'analyst'
        ? orgData.licenses.analyst_seats_used
        : orgData.licenses.bank_seats_used;
      const total = seatType === 'analyst'
        ? orgData.licenses.analyst_seats
        : orgData.licenses.bank_seats;

      if (used >= total) {
        throw new Error(`No ${seatType} seats available (${used}/${total} used)`);
      }

      // Update user
      const userRef = doc(db, 'users', uid);
      transaction.update(userRef, {
        org_id: orgId,
        role,
        status: 'active',
        approved_at: serverTimestamp(),
        approved_by: approvedBy,
      });

      // Increment seat count
      const field = seatType === 'analyst' ? 'licenses.analyst_seats_used' : 'licenses.bank_seats_used';
      transaction.update(orgRef, {
        [field]: increment(1),
        updated_at: serverTimestamp(),
      });
    });

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to approve user'
    };
  }
};

/**
 * Update user status with seat management
 * Handles seat increments/decrements when activating/suspending users
 */
export const updateUserStatusWithSeats = async (
  uid: string,
  newStatus: UserStatus,
  updatedBy: string
): Promise<{ success: boolean; error?: string }> => {
  try {
    const user = await getUserProfile(uid);
    if (!user) {
      throw new Error('User not found');
    }

    const oldStatus = user.status;

    // No change needed
    if (oldStatus === newStatus) {
      return { success: true };
    }

    const seatType = user.role ? getSeatTypeForRole(user.role) : null;
    const orgId = user.org_id;

    // Update user status
    await updateUserProfile(uid, { status: newStatus });

    // Handle seat changes if user has a seat-consuming role
    if (seatType && orgId) {
      if (oldStatus === 'active' && newStatus === 'suspended') {
        // User is being suspended - decrement seat
        await decrementSeatUsage(orgId, seatType);
        await createAuditLog('seat_released', updatedBy, 'user', uid, {
          seatType,
          orgId,
          reason: 'user_suspended',
        });
      } else if (oldStatus === 'suspended' && newStatus === 'active') {
        // User is being reactivated - check and increment seat
        const { available, used, total } = await checkSeatAvailability(orgId, user.role!);
        if (!available) {
          // Rollback status change
          await updateUserProfile(uid, { status: oldStatus });
          throw new Error(`Cannot reactivate: No ${seatType} seats available (${used}/${total} used)`);
        }
        await incrementSeatUsage(orgId, seatType);
        await createAuditLog('seat_consumed', updatedBy, 'user', uid, {
          seatType,
          orgId,
          reason: 'user_reactivated',
        });
      }
    }

    return { success: true };
  } catch (error) {
    return {
      success: false,
      error: error instanceof Error ? error.message : 'Failed to update user status',
    };
  }
};

/**
 * Recalculate seat usage for an organization based on actual active users
 * Useful for fixing inconsistencies
 */
export const recalculateSeatUsage = async (orgId: string): Promise<{ analyst: number; bank: number }> => {
  const users = await getUsersByOrg(orgId);

  let analystCount = 0;
  let bankCount = 0;

  for (const user of users) {
    if (user.status !== 'active' || !user.role) continue;

    const seatType = getSeatTypeForRole(user.role);
    if (seatType === 'analyst') analystCount++;
    if (seatType === 'bank') bankCount++;
  }

  // Update the organization with correct counts
  const docRef = doc(db, 'organizations', orgId);
  await updateDoc(docRef, {
    'licenses.analyst_seats_used': analystCount,
    'licenses.bank_seats_used': bankCount,
    updated_at: serverTimestamp(),
  });

  return { analyst: analystCount, bank: bankCount };
};

// Analyst Assignment Functions

/**
 * Assign an analyst to an institution
 * Creates a new assignment or reactivates an existing inactive one
 */
export const assignAnalystToInstitution = async (
  analystId: string,
  institutionId: string,
  orgId: string,
  assignedBy: string
): Promise<AnalystAssignment> => {
  // Check if there's an existing active assignment for this institution
  const existingActive = await getActiveAssignmentForInstitution(institutionId, orgId);
  if (existingActive) {
    throw new Error(`Institution ${institutionId} is already assigned to another analyst`);
  }

  // Check if this analyst already has an inactive assignment for this institution
  const existingQuery = query(
    collection(db, 'analyst_assignments'),
    where('analyst_id', '==', analystId),
    where('institution_id', '==', institutionId),
    where('org_id', '==', orgId)
  );
  const existingSnap = await getDocs(existingQuery);

  if (!existingSnap.empty) {
    // Reactivate existing assignment
    const existingDoc = existingSnap.docs[0];
    await updateDoc(doc(db, 'analyst_assignments', existingDoc.id), {
      status: 'active',
      assigned_by: assignedBy,
      assigned_at: serverTimestamp(),
    });
    return {
      id: existingDoc.id,
      ...existingDoc.data(),
      status: 'active',
      assigned_by: assignedBy,
    } as AnalystAssignment;
  }

  // Create new assignment
  const assignmentRef = doc(collection(db, 'analyst_assignments'));
  const assignment: Omit<AnalystAssignment, 'id'> = {
    analyst_id: analystId,
    institution_id: institutionId,
    org_id: orgId,
    assigned_by: assignedBy,
    assigned_at: Timestamp.now(),
    status: 'active',
    shared_with: [],
  };

  await setDoc(assignmentRef, assignment);

  return { id: assignmentRef.id, ...assignment };
};

/**
 * Get the active assignment for an institution within an org
 */
export const getActiveAssignmentForInstitution = async (
  institutionId: string,
  orgId: string
): Promise<AnalystAssignment | null> => {
  const q = query(
    collection(db, 'analyst_assignments'),
    where('institution_id', '==', institutionId),
    where('org_id', '==', orgId),
    where('status', '==', 'active')
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docData = snap.docs[0];
  return { id: docData.id, ...docData.data() } as AnalystAssignment;
};

/**
 * Reassign an institution to a different analyst
 */
export const reassignInstitution = async (
  institutionId: string,
  orgId: string,
  newAnalystId: string,
  reassignedBy: string
): Promise<AnalystAssignment> => {
  // Deactivate current assignment
  const currentAssignment = await getActiveAssignmentForInstitution(institutionId, orgId);
  if (currentAssignment) {
    await updateDoc(doc(db, 'analyst_assignments', currentAssignment.id), {
      status: 'inactive',
    });
  }

  // Create new assignment
  return assignAnalystToInstitution(newAnalystId, institutionId, orgId, reassignedBy);
};

/**
 * Get all assignments for an analyst
 */
export const getAnalystAssignments = async (
  analystId: string,
  orgId?: string
): Promise<AnalystAssignment[]> => {
  let q = query(
    collection(db, 'analyst_assignments'),
    where('analyst_id', '==', analystId),
    where('status', '==', 'active')
  );

  if (orgId) {
    q = query(
      collection(db, 'analyst_assignments'),
      where('analyst_id', '==', analystId),
      where('org_id', '==', orgId),
      where('status', '==', 'active')
    );
  }

  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as AnalystAssignment));
};

/**
 * Get all assignments for an organization (inventory view)
 */
export const getOrgAssignments = async (orgId: string): Promise<AnalystAssignment[]> => {
  const q = query(
    collection(db, 'analyst_assignments'),
    where('org_id', '==', orgId),
    where('status', '==', 'active')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as AnalystAssignment));
};

/**
 * Get assignments shared with an analyst
 */
export const getSharedAssignments = async (
  analystId: string,
  orgId?: string
): Promise<AnalystAssignment[]> => {
  let q = query(
    collection(db, 'analyst_assignments'),
    where('shared_with', 'array-contains', analystId),
    where('status', '==', 'active')
  );

  if (orgId) {
    q = query(
      collection(db, 'analyst_assignments'),
      where('shared_with', 'array-contains', analystId),
      where('org_id', '==', orgId),
      where('status', '==', 'active')
    );
  }

  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as AnalystAssignment));
};

/**
 * Share an assignment with other analysts
 */
export const shareAssignmentWith = async (
  assignmentId: string,
  analystIds: string[]
): Promise<void> => {
  const assignmentRef = doc(db, 'analyst_assignments', assignmentId);
  const snap = await getDoc(assignmentRef);
  if (!snap.exists()) {
    throw new Error('Assignment not found');
  }

  const currentShared = snap.data().shared_with || [];
  const newShared = [...new Set([...currentShared, ...analystIds])];

  await updateDoc(assignmentRef, {
    shared_with: newShared,
  });
};

/**
 * Remove sharing from an assignment
 */
export const removeSharing = async (
  assignmentId: string,
  analystId: string
): Promise<void> => {
  const assignmentRef = doc(db, 'analyst_assignments', assignmentId);
  const snap = await getDoc(assignmentRef);
  if (!snap.exists()) {
    throw new Error('Assignment not found');
  }

  const currentShared: string[] = snap.data().shared_with || [];
  const newShared = currentShared.filter(id => id !== analystId);

  await updateDoc(assignmentRef, {
    shared_with: newShared,
  });
};

/**
 * Unassign an analyst from an institution (deactivate)
 */
export const unassignInstitution = async (
  assignmentId: string
): Promise<void> => {
  await updateDoc(doc(db, 'analyst_assignments', assignmentId), {
    status: 'inactive',
  });
};

// Report Workflow Functions

/**
 * Create a new report draft
 */
export const createReportDraft = async (
  institutionId: string,
  period: string,
  analystId: string,
  orgId: string,
  finalDueDate: Date,
  createdBy: string,
  stageDeadlines?: Partial<Omit<ReportDeadline, 'final_due_date'>>
): Promise<ReportWorkflow> => {
  // Check if draft already exists for this institution/period
  const existingQuery = query(
    collection(db, 'report_workflows'),
    where('institution_id', '==', institutionId),
    where('period', '==', period),
    where('org_id', '==', orgId)
  );
  const existingSnap = await getDocs(existingQuery);

  if (!existingSnap.empty) {
    const existing = existingSnap.docs[0].data();
    if (existing.status !== 'approved' && existing.status !== 'rejected') {
      throw new Error(`A draft already exists for ${institutionId} - ${period}`);
    }
  }

  // Calculate version number
  const allVersionsQuery = query(
    collection(db, 'report_workflows'),
    where('institution_id', '==', institutionId),
    where('period', '==', period),
    where('org_id', '==', orgId)
  );
  const allVersionsSnap = await getDocs(allVersionsQuery);
  const version = allVersionsSnap.size + 1;

  const reportRef = doc(collection(db, 'report_workflows'));

  const deadline: ReportDeadline = {
    final_due_date: Timestamp.fromDate(finalDueDate),
    draft_due: stageDeadlines?.draft_due || null,
    admin_review_due: stageDeadlines?.admin_review_due || null,
    client_review_due: stageDeadlines?.client_review_due || null,
    committee_review_due: stageDeadlines?.committee_review_due || null,
  };

  const report: Omit<ReportWorkflow, 'id'> = {
    institution_id: institutionId,
    period,
    analyst_id: analystId,
    org_id: orgId,
    status: 'draft',
    version,
    deadline,
    content: {
      sections: {},
      notes: '',
      attachments: [],
    },
    created_at: Timestamp.now(),
    created_by: createdBy,
    updated_at: Timestamp.now(),
    updated_by: createdBy,
    ai_generated_at: null,
    ai_generation_model: null,
    first_edited_at: null,
    first_edited_by: null,
    submitted_at: null,
    admin_reviewed_at: null,
    admin_reviewed_by: null,
    admin_comments: null,
    client_review_started_at: null,
    client_reviewed_at: null,
    client_reviewed_by: null,
    client_comments: [],
    client_additional_info: null,
    committee_reviewed_at: null,
    committee_reviewed_by: null,
    committee_comments: null,
    published_at: null,
    rejected_at: null,
    rejected_by: null,
    rejection_reason: null,
    rejection_stage: null,
  };

  await setDoc(reportRef, report);

  return { id: reportRef.id, ...report };
};

/**
 * Get a report workflow by ID
 */
export const getReportWorkflow = async (reportId: string): Promise<ReportWorkflow | null> => {
  const docRef = doc(db, 'report_workflows', reportId);
  const docSnap = await getDoc(docRef);
  if (!docSnap.exists()) return null;
  return { id: docSnap.id, ...docSnap.data() } as ReportWorkflow;
};

/**
 * Get report for an institution and period
 */
export const getReportForInstitutionPeriod = async (
  institutionId: string,
  period: string,
  orgId: string
): Promise<ReportWorkflow | null> => {
  const q = query(
    collection(db, 'report_workflows'),
    where('institution_id', '==', institutionId),
    where('period', '==', period),
    where('org_id', '==', orgId),
    orderBy('version', 'desc')
  );
  const snap = await getDocs(q);
  if (snap.empty) return null;
  const docData = snap.docs[0];
  return { id: docData.id, ...docData.data() } as ReportWorkflow;
};

/**
 * Update report draft content
 */
export const updateReportDraft = async (
  reportId: string,
  content: Partial<ReportDraftContent>,
  updatedBy: string
): Promise<void> => {
  const docRef = doc(db, 'report_workflows', reportId);
  const updates: Record<string, unknown> = {
    updated_at: serverTimestamp(),
    updated_by: updatedBy,
  };

  if (content.sections) {
    updates['content.sections'] = content.sections;
  }
  if (content.notes !== undefined) {
    updates['content.notes'] = content.notes;
  }
  if (content.attachments) {
    updates['content.attachments'] = content.attachments;
  }

  await updateDoc(docRef, updates);
};

/**
 * Update a specific section in a report
 */
export const updateReportSection = async (
  reportId: string,
  sectionId: string,
  sectionData: Partial<SectionDraft>,
  updatedBy: string
): Promise<void> => {
  const docRef = doc(db, 'report_workflows', reportId);
  const report = await getReportWorkflow(reportId);
  if (!report) throw new Error('Report not found');

  const existingSection = report.content.sections[sectionId] || {
    id: sectionId,
    title: sectionData.title || sectionId,
    content: '',
    ai_generated: false,
    edited: false,
    last_edited_by: updatedBy,
    last_edited_at: Timestamp.now(),
  };

  const updatedSection: SectionDraft = {
    ...existingSection,
    ...sectionData,
    last_edited_by: updatedBy,
    last_edited_at: Timestamp.now(),
    edited: true,
  };

  // Build update object
  const updateData: Record<string, unknown> = {
    [`content.sections.${sectionId}`]: updatedSection,
    updated_at: serverTimestamp(),
    updated_by: updatedBy,
  };

  // Track first edit if this is the first save after AI generation
  if (report.ai_generated_at && !report.first_edited_at) {
    updateData.first_edited_at = serverTimestamp();
    updateData.first_edited_by = updatedBy;
  }

  await updateDoc(docRef, updateData);
};

/**
 * Update report deadline
 */
export const updateReportDeadline = async (
  reportId: string,
  deadline: Partial<ReportDeadline>,
  updatedBy: string
): Promise<void> => {
  const docRef = doc(db, 'report_workflows', reportId);
  const updates: Record<string, unknown> = {
    updated_at: serverTimestamp(),
    updated_by: updatedBy,
  };

  if (deadline.final_due_date) {
    updates['deadline.final_due_date'] = deadline.final_due_date;
  }
  if (deadline.draft_due !== undefined) {
    updates['deadline.draft_due'] = deadline.draft_due;
  }
  if (deadline.admin_review_due !== undefined) {
    updates['deadline.admin_review_due'] = deadline.admin_review_due;
  }
  if (deadline.client_review_due !== undefined) {
    updates['deadline.client_review_due'] = deadline.client_review_due;
  }
  if (deadline.committee_review_due !== undefined) {
    updates['deadline.committee_review_due'] = deadline.committee_review_due;
  }

  await updateDoc(docRef, updates);
};

/**
 * Submit report for admin review
 */
export const submitForAdminReview = async (
  reportId: string,
  submittedBy: string
): Promise<void> => {
  const docRef = doc(db, 'report_workflows', reportId);
  await updateDoc(docRef, {
    status: 'pending_admin',
    submitted_at: serverTimestamp(),
    updated_at: serverTimestamp(),
    updated_by: submittedBy,
    // Clear any previous rejection
    rejected_at: null,
    rejected_by: null,
    rejection_reason: null,
    rejection_stage: null,
  });
};

/**
 * Admin approve report (moves to client_review)
 */
export const adminApproveReport = async (
  reportId: string,
  adminId: string,
  comments?: string
): Promise<void> => {
  const docRef = doc(db, 'report_workflows', reportId);
  await updateDoc(docRef, {
    status: 'client_review',
    admin_reviewed_at: serverTimestamp(),
    admin_reviewed_by: adminId,
    admin_comments: comments || null,
    client_review_started_at: serverTimestamp(),
    updated_at: serverTimestamp(),
    updated_by: adminId,
  });
};

/**
 * Admin reject report (sets status to rejected)
 */
export const adminRejectReport = async (
  reportId: string,
  adminId: string,
  reason: string
): Promise<void> => {
  const docRef = doc(db, 'report_workflows', reportId);
  await updateDoc(docRef, {
    status: 'rejected',
    admin_reviewed_at: serverTimestamp(),
    admin_reviewed_by: adminId,
    admin_comments: reason,
    rejected_at: serverTimestamp(),
    rejected_by: adminId,
    rejection_reason: reason,
    rejection_stage: 'admin',
    updated_at: serverTimestamp(),
    updated_by: adminId,
  });
};

/**
 * Add client comment
 */
export const addClientComment = async (
  reportId: string,
  authorId: string,
  authorName: string,
  content: string,
  sectionId?: string
): Promise<ClientComment> => {
  const docRef = doc(db, 'report_workflows', reportId);
  const report = await getReportWorkflow(reportId);
  if (!report) throw new Error('Report not found');

  const comment: ClientComment = {
    id: `comment_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`,
    author_id: authorId,
    author_name: authorName,
    content,
    section_id: sectionId || null,
    created_at: Timestamp.now(),
    resolved: false,
    resolved_by: null,
    resolved_at: null,
  };

  await updateDoc(docRef, {
    client_comments: [...report.client_comments, comment],
    updated_at: serverTimestamp(),
    updated_by: authorId,
  });

  return comment;
};

/**
 * Resolve a client comment
 */
export const resolveClientComment = async (
  reportId: string,
  commentId: string,
  resolvedBy: string
): Promise<void> => {
  const docRef = doc(db, 'report_workflows', reportId);
  const report = await getReportWorkflow(reportId);
  if (!report) throw new Error('Report not found');

  const updatedComments = report.client_comments.map(c => {
    if (c.id === commentId) {
      return {
        ...c,
        resolved: true,
        resolved_by: resolvedBy,
        resolved_at: Timestamp.now(),
      };
    }
    return c;
  });

  await updateDoc(docRef, {
    client_comments: updatedComments,
    updated_at: serverTimestamp(),
    updated_by: resolvedBy,
  });
};

/**
 * Add client additional information
 */
export const addClientAdditionalInfo = async (
  reportId: string,
  additionalInfo: string,
  clientId: string
): Promise<void> => {
  const docRef = doc(db, 'report_workflows', reportId);
  await updateDoc(docRef, {
    client_additional_info: additionalInfo,
    updated_at: serverTimestamp(),
    updated_by: clientId,
  });
};

/**
 * Complete client review (moves to pending_committee)
 */
export const completeClientReview = async (
  reportId: string,
  clientId: string
): Promise<void> => {
  const docRef = doc(db, 'report_workflows', reportId);
  await updateDoc(docRef, {
    status: 'pending_committee',
    client_reviewed_at: serverTimestamp(),
    client_reviewed_by: clientId,
    updated_at: serverTimestamp(),
    updated_by: clientId,
  });
};

/**
 * Skip client review (admin can move directly to committee)
 */
export const skipClientReview = async (
  reportId: string,
  adminId: string
): Promise<void> => {
  const docRef = doc(db, 'report_workflows', reportId);
  await updateDoc(docRef, {
    status: 'pending_committee',
    client_reviewed_at: null,
    client_reviewed_by: null,
    updated_at: serverTimestamp(),
    updated_by: adminId,
  });
};

/**
 * Committee approve report (final approval)
 */
export const committeeApproveReport = async (
  reportId: string,
  committeeId: string,
  comments?: string
): Promise<void> => {
  const docRef = doc(db, 'report_workflows', reportId);
  await updateDoc(docRef, {
    status: 'approved',
    committee_reviewed_at: serverTimestamp(),
    committee_reviewed_by: committeeId,
    committee_comments: comments || null,
    published_at: serverTimestamp(),
    updated_at: serverTimestamp(),
    updated_by: committeeId,
  });
};

/**
 * Committee reject report (sets status to rejected)
 */
export const committeeRejectReport = async (
  reportId: string,
  committeeId: string,
  reason: string
): Promise<void> => {
  const docRef = doc(db, 'report_workflows', reportId);
  await updateDoc(docRef, {
    status: 'rejected',
    committee_reviewed_at: serverTimestamp(),
    committee_reviewed_by: committeeId,
    committee_comments: reason,
    rejected_at: serverTimestamp(),
    rejected_by: committeeId,
    rejection_reason: reason,
    rejection_stage: 'committee',
    updated_at: serverTimestamp(),
    updated_by: committeeId,
  });
};

/**
 * Get reports by status for an organization
 */
export const getReportsByStatus = async (
  orgId: string,
  status: ReportWorkflowStatus
): Promise<ReportWorkflow[]> => {
  const q = query(
    collection(db, 'report_workflows'),
    where('org_id', '==', orgId),
    where('status', '==', status),
    orderBy('updated_at', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as ReportWorkflow));
};

/**
 * Get reports for an organization (all statuses)
 */
export const getOrgReports = async (
  orgId: string
): Promise<ReportWorkflow[]> => {
  const q = query(
    collection(db, 'report_workflows'),
    where('org_id', '==', orgId),
    orderBy('updated_at', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as ReportWorkflow));
};

/**
 * Get analyst's drafts
 */
export const getAnalystDrafts = async (
  analystId: string
): Promise<ReportWorkflow[]> => {
  const q = query(
    collection(db, 'report_workflows'),
    where('analyst_id', '==', analystId),
    orderBy('updated_at', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as ReportWorkflow));
};

/**
 * Get overdue reports for an organization
 */
export const getOverdueReports = async (
  orgId: string
): Promise<ReportWorkflow[]> => {
  const now = Timestamp.now();
  const q = query(
    collection(db, 'report_workflows'),
    where('org_id', '==', orgId),
    where('status', 'in', ['draft', 'pending_admin', 'client_review', 'pending_committee'])
  );
  const snap = await getDocs(q);

  return snap.docs
    .map(d => ({ id: d.id, ...d.data() } as ReportWorkflow))
    .filter(r => r.deadline.final_due_date.toMillis() < now.toMillis());
};

/**
 * Get reports due within N days
 */
export const getReportsDueSoon = async (
  orgId: string,
  daysFromNow: number
): Promise<ReportWorkflow[]> => {
  const now = new Date();
  const futureDate = new Date(now.getTime() + daysFromNow * 24 * 60 * 60 * 1000);

  const q = query(
    collection(db, 'report_workflows'),
    where('org_id', '==', orgId),
    where('status', 'in', ['draft', 'pending_admin', 'client_review', 'pending_committee'])
  );
  const snap = await getDocs(q);

  return snap.docs
    .map(d => ({ id: d.id, ...d.data() } as ReportWorkflow))
    .filter(r => {
      const dueDate = r.deadline.final_due_date.toDate();
      return dueDate >= now && dueDate <= futureDate;
    });
};

/**
 * Get reports pending client review for a bank organization
 */
export const getReportsForClientReview = async (
  institutionIds: string[]
): Promise<ReportWorkflow[]> => {
  if (institutionIds.length === 0) return [];

  const q = query(
    collection(db, 'report_workflows'),
    where('institution_id', 'in', institutionIds),
    where('status', '==', 'client_review')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as ReportWorkflow));
};

/**
 * Get all reports for a specific institution
 */
export const getReportsForInstitution = async (
  institutionId: string,
  orgId: string
): Promise<ReportWorkflow[]> => {
  const q = query(
    collection(db, 'report_workflows'),
    where('institution_id', '==', institutionId),
    where('org_id', '==', orgId),
    orderBy('updated_at', 'desc')
  );
  const snap = await getDocs(q);
  return snap.docs.map(d => ({ id: d.id, ...d.data() } as ReportWorkflow));
};

/**
 * Get reports count by status for an organization
 */
export const getReportStatusCounts = async (
  orgId: string
): Promise<Record<ReportWorkflowStatus, number>> => {
  const reports = await getOrgReports(orgId);
  const counts: Record<ReportWorkflowStatus, number> = {
    draft: 0,
    pending_admin: 0,
    client_review: 0,
    pending_committee: 0,
    approved: 0,
    rejected: 0,
  };

  for (const report of reports) {
    counts[report.status]++;
  }

  return counts;
};

/**
 * Revise a rejected report - creates a new draft version
 * Copies content from rejected report but resets workflow status
 */
export const reviseRejectedReport = async (
  reportId: string,
  revisedBy: string
): Promise<ReportWorkflow> => {
  const rejectedReport = await getReportWorkflow(reportId);
  if (!rejectedReport) {
    throw new Error('Report not found');
  }

  if (rejectedReport.status !== 'rejected') {
    throw new Error('Only rejected reports can be revised');
  }

  // Calculate new version number
  const allVersionsQuery = query(
    collection(db, 'report_workflows'),
    where('institution_id', '==', rejectedReport.institution_id),
    where('period', '==', rejectedReport.period),
    where('org_id', '==', rejectedReport.org_id)
  );
  const allVersionsSnap = await getDocs(allVersionsQuery);
  const newVersion = allVersionsSnap.size + 1;

  // Create a new draft with content from the rejected report
  const reportRef = doc(collection(db, 'report_workflows'));

  const newReport: Omit<ReportWorkflow, 'id'> = {
    institution_id: rejectedReport.institution_id,
    period: rejectedReport.period,
    analyst_id: rejectedReport.analyst_id,
    org_id: rejectedReport.org_id,
    status: 'draft',
    version: newVersion,
    deadline: rejectedReport.deadline, // Keep the same deadline
    content: rejectedReport.content, // Copy content from rejected report
    created_at: Timestamp.now(),
    created_by: revisedBy,
    updated_at: Timestamp.now(),
    updated_by: revisedBy,
    submitted_at: null,
    admin_reviewed_at: null,
    admin_reviewed_by: null,
    admin_comments: null,
    client_review_started_at: null,
    client_reviewed_at: null,
    client_reviewed_by: null,
    client_comments: [],
    client_additional_info: null,
    committee_reviewed_at: null,
    committee_reviewed_by: null,
    committee_comments: null,
    published_at: null,
    rejected_at: null,
    rejected_by: null,
    rejection_reason: null,
    rejection_stage: null,
  };

  await setDoc(reportRef, newReport);

  return { id: reportRef.id, ...newReport };
};
